import { formatAiChatMarkdown } from './markdown.js';
import { scrollAiChatToBottom } from './scroll.js';

const aiChatTextStreamState: { id: number; timer: ReturnType<typeof setTimeout> | null } = {
  id: 0,
  timer: null
};
let aiChatBubbleGrowTimer: ReturnType<typeof setTimeout> | null = null;

export function cancelAiChatTextStream(): void {
  aiChatTextStreamState.id += 1;
  if (aiChatTextStreamState.timer) {
    clearTimeout(aiChatTextStreamState.timer);
    aiChatTextStreamState.timer = null;
  }
}

function splitAiStreamChunks(text: string): string[] {
  const s = String(text || '');
  if (!s) return [];
  const parts = s.match(/\n+|\S+\s*/g);
  return parts && parts.length ? parts : [s];
}

function aiChatPrefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function finishAiChatStreamText(textEl: HTMLElement | null, fullText: string): void {
  if (!textEl) return;
  const bubble = textEl.closest('.chat-bubble');
  textEl.innerHTML = formatAiChatMarkdown(fullText);
  textEl.classList.remove('chat-bubble-text--streaming');
  if (bubble) {
    bubble.classList.remove('chat-bubble--stream-grow');
    pulseAiChatBubbleGrow(bubble as HTMLElement, { finalize: true });
  }
}

function appendAiStreamChunk(textEl: HTMLElement, chunk: string): void {
  if (!textEl || chunk == null || chunk === '') return;
  const span = document.createElement('span');
  span.className = 'ai-stream-chunk';
  if (!aiChatPrefersReducedMotion()) span.classList.add('ai-stream-chunk--in');
  span.textContent = chunk;
  textEl.appendChild(span);
}

function releaseAiChatBubbleGrowStyles(bubble: HTMLElement): void {
  bubble.style.transition = '';
  bubble.style.height = '';
  bubble.style.overflow = '';
  bubble.style.willChange = '';
}

function pulseAiChatBubbleGrow(bubble: HTMLElement, options?: { finalize?: boolean }): void {
  options = options || {};
  if (!bubble || aiChatPrefersReducedMotion()) return;

  const startH = bubble.offsetHeight;
  bubble.style.overflow = 'hidden';
  bubble.style.willChange = 'height';
  bubble.style.transition = 'none';
  bubble.style.height = 'auto';
  const targetH = bubble.offsetHeight;

  if (!options.finalize && targetH <= startH + 1) {
    releaseAiChatBubbleGrowStyles(bubble);
    return;
  }

  bubble.style.height = startH + 'px';
  requestAnimationFrame(function () {
    requestAnimationFrame(function () {
      bubble.style.transition = 'height 0.38s cubic-bezier(0.16, 0.9, 0.22, 1)';
      bubble.style.height = targetH + 'px';
      if (aiChatBubbleGrowTimer) clearTimeout(aiChatBubbleGrowTimer);
      aiChatBubbleGrowTimer = setTimeout(function () {
        aiChatBubbleGrowTimer = null;
        if (bubble.classList.contains('chat-bubble--stream-grow')) {
          bubble.style.transition = '';
          bubble.style.height = 'auto';
          bubble.style.overflow = 'hidden';
          return;
        }
        releaseAiChatBubbleGrowStyles(bubble);
      }, 400);
    });
  });
}

export function streamAiChatBubbleText(
  textEl: HTMLElement | null,
  fullText: string,
  options?: { instant?: boolean }
): Promise<void> {
  options = options || {};
  cancelAiChatTextStream();
  const streamId = aiChatTextStreamState.id;
  const full = String(fullText || '');

  return new Promise<void>(function (resolve) {
    if (!textEl || !full) {
      finishAiChatStreamText(textEl, full);
      resolve();
      return;
    }

    const reducedMotion = aiChatPrefersReducedMotion();
    const chunks = splitAiStreamChunks(full);
    if (reducedMotion || options.instant || chunks.length <= 1) {
      finishAiChatStreamText(textEl, full);
      resolve();
      return;
    }

    textEl.classList.add('chat-bubble-text--streaming');
    textEl.innerHTML = '';
    const bubble = textEl.closest('.chat-bubble') as HTMLElement | null;
    const streamTextEl = textEl;
    if (bubble) bubble.classList.add('chat-bubble--stream-grow');
    let idx = 0;
    let scrollTick = 0;

    function done(): void {
      if (streamId !== aiChatTextStreamState.id) {
        resolve();
        return;
      }
      finishAiChatStreamText(textEl, full);
      scrollAiChatToBottom();
      resolve();
    }

    function step(): void {
      if (streamId !== aiChatTextStreamState.id) {
        resolve();
        return;
      }
      if (idx >= chunks.length) {
        done();
        return;
      }
      appendAiStreamChunk(streamTextEl, chunks[idx]);
      idx += 1;
      if (bubble) pulseAiChatBubbleGrow(bubble);
      scrollTick += 1;
      if (scrollTick % 2 === 0) scrollAiChatToBottom();
      const left = chunks.length - idx;
      const delay = left > 220 ? 9 : left > 100 ? 14 : left > 40 ? 20 : 28;
      aiChatTextStreamState.timer = setTimeout(step, delay);
    }

    if (bubble) {
      bubble.addEventListener(
        'click',
        function () {
          if (streamId !== aiChatTextStreamState.id) return;
          if (aiChatTextStreamState.timer) {
            clearTimeout(aiChatTextStreamState.timer);
            aiChatTextStreamState.timer = null;
          }
          done();
        },
        { once: true }
      );
    }

    step();
  });
}
